import type { FC, ReactNode } from 'react'
import type { Course, Instructor } from '@/types'

import Link from 'next/link'
import { courseHref } from '@/lib/routes'
import { CmsImage } from '@/components/generals/cms-image'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'

interface InstructorsSectionProps {
    instructors: Array<Instructor>
    courses: Array<Course>
}

export const InstructorsSection: FC<InstructorsSectionProps> = ({ instructors, courses }): ReactNode => {
    if (instructors.length === 0) return null

    return (
        <section id='instructors' aria-labelledby='instructors-title' className='bg-secondary/50 py-20 sm:py-28'>
            <Container>
                <div className='flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        id='instructors-title'
                        eyebrow="Instructors"
                        title="Learn from people who use these skills every day"
                    />

                    <p className='max-w-md text-base leading-relaxed text-muted-foreground lg:text-right'>
                        Every trainer teaches small groups and stays with your batch from the first lesson to the final project.
                    </p>
                </div>

                <div className='mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                    {instructors.map(instructor => {
                        const taught = courses.filter(course => course.instructor?.id === instructor.id)

                        return (
                            <article key={instructor.id} className='flex flex-col overflow-hidden rounded-3xl border bg-card shadow-sm shadow-foreground/5'>
                                <div className='relative aspect-[6/5] bg-secondary'>
                                    <CmsImage
                                        src={instructor.photo}
                                        alt={instructor.photo_alt}
                                        sizes='(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw'
                                        className='object-[center_25%]'
                                    />
                                </div>

                                <div className='flex flex-1 flex-col p-6 sm:p-7'>
                                    <h3 className='text-xl font-semibold' data-mdlfy-cms={`instructors:${instructor.id}:name`}>
                                        {instructor.name}
                                    </h3>

                                    <p className='mt-1 text-sm font-medium text-primary' data-mdlfy-cms={`instructors:${instructor.id}:role`}>
                                        {instructor.role}
                                    </p>

                                    {instructor.bio && (
                                        <p className='mt-4 text-sm leading-relaxed text-muted-foreground' data-mdlfy-cms={`instructors:${instructor.id}:bio`}>
                                            {instructor.bio}
                                        </p>
                                    )}

                                    {taught.length > 0 && (
                                        <div className='mt-auto pt-6'>
                                            <p className='font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase'>
                                                Teaches
                                            </p>

                                            <ul className='mt-2.5 flex flex-wrap gap-2'>
                                                {taught.map(course => (
                                                    <li key={course.id}>
                                                        <Link
                                                            href={courseHref(course.slug)}
                                                            className='inline-flex rounded-full border px-3 py-1 text-xs font-medium transition-colors hover:border-primary hover:text-primary focus-visible:outline-2 focus-visible:outline-ring'
                                                        >
                                                            <span data-mdlfy-cms={`courses:${course.id}:title`}>{course.title}</span>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </article>
                        )
                    })}
                </div>
            </Container>
        </section>
    )
}
